import React from "react";
import Spinner from "../../components/common/Spinner";
import ErrorAlert from "../../components/common/ErrorAlert";

interface GenericReportViewerProps {
  title: string;
  loading: boolean;
  error: string | null;
  report: any;
  emptyMessage?: string;
}

const formatValue = (v: any): string => {
  if (v === null || v === undefined) return "—";
  if (typeof v === "number") {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      maximumFractionDigits: 2,
    }).format(v);
  }
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

const isNumericField = (key: string, value: any): boolean =>
  typeof value === "number" &&
  !/id$/i.test(key) &&
  !/^count$/i.test(key) &&
  !/days/i.test(key);

const Table: React.FC<{ rows: any[] }> = ({ rows }) => {
  if (!rows.length)
    return <p className="text-sm text-gray-500 italic">No rows.</p>;
  const cols = Array.from(
    rows.reduce<Set<string>>((acc, r) => {
      Object.keys(r || {}).forEach((k) => acc.add(k));
      return acc;
    }, new Set()),
  );
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50">
          <tr>
            {cols.map((c) => (
              <th
                key={c}
                className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-100">
          {rows.map((row, idx) => (
            <tr key={idx} className="hover:bg-gray-50">
              {cols.map((c) => {
                const v = row?.[c];
                const numeric = isNumericField(c, v);
                return (
                  <td
                    key={c}
                    className={`px-3 py-2 whitespace-nowrap ${
                      numeric ? "text-right tabular-nums" : ""
                    }`}
                  >
                    {numeric || typeof v === "number"
                      ? formatValue(v)
                      : formatValue(v)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ScalarsGrid: React.FC<{ data: Record<string, any> }> = ({ data }) => {
  const entries = Object.entries(data).filter(
    ([, v]) => v === null || typeof v !== "object",
  );
  if (!entries.length) return null;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-6">
      {entries.map(([k, v]) => (
        <div key={k} className="bg-gray-50 rounded p-3">
          <p className="text-xs uppercase text-gray-500">{k}</p>
          <p className="text-base font-semibold text-gray-900">
            {formatValue(v)}
          </p>
        </div>
      ))}
    </div>
  );
};

const Section: React.FC<{ label: string; data: any }> = ({ label, data }) => {
  if (data === null || data === undefined) return null;
  if (Array.isArray(data)) {
    if (!data.length) return null;
    if (typeof data[0] === "object" && data[0] !== null) {
      return (
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-gray-700 uppercase mb-2">
            {label}
          </h3>
          <Table rows={data} />
        </div>
      );
    }
    return (
      <div className="mb-6">
        <h3 className="text-sm font-semibold text-gray-700 uppercase mb-2">
          {label}
        </h3>
        <p className="text-sm text-gray-900">
          {data.map(formatValue).join(", ")}
        </p>
      </div>
    );
  }
  if (typeof data === "object") {
    return (
      <div className="mb-6">
        <h3 className="text-sm font-semibold text-gray-700 uppercase mb-2">
          {label}
        </h3>
        <ScalarsGrid data={data} />
        {Object.entries(data)
          .filter(([, v]) => v && typeof v === "object")
          .map(([k, v]) => (
            <Section key={k} label={k} data={v} />
          ))}
      </div>
    );
  }
  return null;
};

const GenericReportViewer: React.FC<GenericReportViewerProps> = ({
  title,
  loading,
  error,
  report,
  emptyMessage,
}) => {
  if (loading) return <Spinner message={`Loading ${title}…`} />;
  if (error) return <ErrorAlert message={error} />;
  if (!report)
    return (
      <ErrorAlert message={emptyMessage || `No ${title} data available`} />
    );

  const scalarEntries = Object.entries(report).filter(
    ([, v]) => v === null || typeof v !== "object",
  );
  const objectEntries = Object.entries(report).filter(
    ([, v]) => v && typeof v === "object",
  );

  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-4">{title}</h2>
      {scalarEntries.length > 0 && (
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <ScalarsGrid data={Object.fromEntries(scalarEntries)} />
        </div>
      )}
      {objectEntries.map(([k, v]) => (
        <div key={k} className="bg-white shadow rounded-lg p-6 mb-6">
          <Section label={k} data={v} />
        </div>
      ))}
    </div>
  );
};

export default GenericReportViewer;
